import { useEffect, useRef } from 'react';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const authHeaders = () => {
  const token = localStorage.getItem('session_token');
  return { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) };
};

const post = (path, body) =>
  fetch(`${API_BASE}/api/use-log/${path}`, {
    method: 'POST', headers: authHeaders(), credentials: 'include', body: JSON.stringify(body || {}),
  }).catch(() => {});

export const trackUse = (type, detail = {}) => post('event', { type, detail });

// Debounced per-field "pick" (typing 1→15→150 logs once as 150)
// Only log picks after a real user gesture (ignores state hydration / auto lookups on load)
let userActed = false;
// Call on app-shell mount so gestures on the login form don't count
export const resetPickGate = () => { userActed = false; };
if (typeof window !== 'undefined') {
  const mark = (e) => { if (e.isTrusted) userActed = true; };
  ['pointerdown', 'keydown'].forEach((t) => window.addEventListener(t, mark, { capture: true, passive: true }));
}
const pickTimers = {};
const lastPick = {};
export const trackPick = (module, field, value, extra = {}) => {
  const key = `${module}|${field}`;
  if (!userActed) { lastPick[key] = typeof value === 'object' ? JSON.stringify(value) : String(value ?? ''); return; }
  clearTimeout(pickTimers[key]);
  pickTimers[key] = setTimeout(() => {
    const v = typeof value === 'object' ? JSON.stringify(value) : String(value ?? '');
    if (!v || lastPick[key] === v) return;
    lastPick[key] = v;
    trackUse('pick', { module, field, value: v.slice(0, 200), ...extra });
  }, 1500);
};

// Logs a pick whenever `value` changes after first render
export const useTrackPick = (module, field, value, extra = {}) => {
  const first = useRef(true);
  const extraRef = useRef(extra);
  extraRef.current = extra;
  useEffect(() => {
    if (first.current) { first.current = false; return; }
    trackPick(module, field, value, extraRef.current);
  }, [module, field, value]);
};

// Context the server attaches to the document it logs for this request
export const detailHeader = (detail) => ({ 'X-Use-Detail': JSON.stringify(detail) });

// Ping every 60 s while the tab is visible; returns a cleanup fn.
export const startUseHeartbeat = () => {
  const beat = () => { if (document.visibilityState === 'visible') post('heartbeat'); };
  beat();
  const id = setInterval(beat, 60000);
  document.addEventListener('visibilitychange', beat);
  return () => { clearInterval(id); document.removeEventListener('visibilitychange', beat); };
};

export { authHeaders };
